import React, { useState } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { 
  MessageCircle, 
  Send, 
  Heart, 
  Reply, 
  ChevronDown, 
  ChevronUp,
  MoreHorizontal,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CommentItem({ comment, spotId, onReply, onLike, depth = 0 }) {
  const [showReplies, setShowReplies] = useState(depth < 1);
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      await onLike(comment.id);
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const handleReply = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!replyText.trim()) return;
    
    try {
      await onReply(comment.id, replyText.trim());
      setReplyText('');
      setIsReplying(false);
    } catch (error) {
      console.error('Error replying to comment:', error);
    }
  };

  const replies = comment.replies || [];
  const hasReplies = replies.length > 0;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 border-l-2 border-gray-100 pl-4' : ''}`}>
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-semibold text-sm">
              {(comment.author || comment.author_name || 'A')[0].toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{comment.author || comment.author_name || 'Anonyme'}</p>
              <p className="text-xs text-gray-400">{formatDate(comment.timestamp || comment.created_at || comment.date)}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <p className="text-gray-700 text-sm leading-relaxed mb-3 whitespace-pre-wrap">
          {comment.content || comment.comment}
        </p>

        {/* Actions */}
        <div className="flex items-center space-x-4 text-sm">
          <button 
            onClick={handleLike}
            className={`flex items-center space-x-1.5 transition-colors ${
              isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
            }`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            <span>{(comment.likes || 0) + (isLiked ? 1 : 0)}</span>
          </button>
          
          <button 
            onClick={() => {
              if (!isAuthenticated) {
                navigate('/login');
                return;
              }
              setIsReplying(!isReplying);
            }}
            className="flex items-center space-x-1.5 text-gray-400 hover:text-blue-500 transition-colors"
          >
            <Reply className="w-4 h-4" />
            <span>Répondre</span>
          </button>

          {hasReplies && (
            <button 
              onClick={() => setShowReplies(!showReplies)}
              className="flex items-center space-x-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showReplies ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span>{replies.length} réponse{replies.length > 1 ? 's' : ''}</span>
            </button>
          )}
        </div>

        {/* Reply Input */}
        {isReplying && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex space-x-2">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Écrire une réponse..."
                className="flex-1 min-h-[60px] text-sm resize-none"
                rows={2}
              />
              <div className="flex flex-col space-y-1">
                <Button 
                  onClick={handleReply}
                  disabled={!replyText.trim()}
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => setIsReplying(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400"
                >
                  ✕
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Replies */}
      {showReplies && hasReplies && (
        <div className="mt-2 space-y-2">
          {replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              spotId={spotId}
              onReply={onReply}
              onLike={onLike}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommentsSection({ spotId, comments = [], onCommentAdded }) {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localComments, setLocalComments] = useState(comments);
  const { isAuthenticated, user, token } = useAuth();
  const navigate = useNavigate();

  // Sync with props
  React.useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  const handleAddComment = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!newComment.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // L'API attend content comme query parameter
      const response = await fetch(`${API_URL}/api/spots-v2/${spotId}/comments?content=${encodeURIComponent(newComment.trim())}`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (response.ok) {
        const newCommentData = await response.json();
        // Transformer le format pour l'affichage
        const formattedComment = {
          id: newCommentData.id,
          author: newCommentData.author_name || user?.username || 'Anonyme',
          content: newCommentData.content,
          timestamp: newCommentData.created_at || new Date().toISOString(),
          likes: 0,
          replies: []
        };
        setLocalComments(prev => [formattedComment, ...prev]);
        setNewComment('');
        if (onCommentAdded) onCommentAdded(formattedComment);
      } else {
        const errorData = await response.text();
        console.error('Error response:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/spots-v2/${spotId}/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setLocalComments(prev => prev.map(c => 
          c.id === commentId ? {...c, likes: data.likes} : c
        ));
      }
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const handleReplyToComment = async (parentCommentId, replyContent) => {
    try {
      const response = await fetch(`${API_URL}/api/spots-v2/${spotId}/comments/${parentCommentId}/replies?content=${encodeURIComponent(replyContent)}`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (response.ok) {
        const newReply = await response.json();
        // Update local state - add reply to the parent comment
        setLocalComments(prev => prev.map(c => {
          if (c.id === parentCommentId) {
            return {
              ...c,
              replies: [...(c.replies || []), newReply]
            };
          }
          return c;
        }));
      } else {
        console.error('Error response:', response.status);
      }
    } catch (error) {
      console.error('Error replying to comment:', error);
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-gray-800 text-lg flex items-center">
          <MessageCircle className="w-5 h-5 mr-2 text-orange-500" />
          Commentaires
          <span className="ml-2 text-sm font-normal text-gray-400">
            ({localComments.length})
          </span>
        </h3>
      </div>

      {/* Add Comment */}
      <div className="mb-6">
        <div className="flex space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
            {isAuthenticated && user ? (user.username || user.email || 'U')[0].toUpperCase() : <User className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={isAuthenticated ? "Partagez votre expérience sur ce spot..." : "Connectez-vous pour commenter..."}
              className="w-full min-h-[80px] resize-none border-gray-200 focus:border-orange-300 focus:ring-orange-200 rounded-xl"
              disabled={!isAuthenticated}
              rows={3}
            />
            <div className="flex justify-end mt-2">
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || isSubmitting || !isAuthenticated}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Publier
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        {localComments.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">
              Aucun commentaire pour le moment.
              <br />
              <span className="text-orange-500">Soyez le premier à partager votre expérience !</span>
            </p>
          </div>
        ) : (
          localComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              spotId={spotId}
              onReply={handleReplyToComment}
              onLike={handleLikeComment}
            />
          ))
        )}
      </div>
    </div>
  );
}
